/* React */
import { useEffect, useState } from 'react'

/* Functions */
import { get, post } from 'src/functions/fetch'

/* Options */
import config from 'src/config'

/* Types */
import { isMenu, item, tMenu } from 'src/shared/frontendTypes/navigation'

/* Menu from preval */
import Build from 'src/layout/Navigation/build.preval'

const navigationCacheKey = `navigation-paths:${process.env.NEXT_PUBLIC_MENU || 'default'}`

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const cleanNavigationTitle = (value: unknown) => {
  const title = typeof value === 'string'
    ? value.trim()
    : typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : ''

  return title === '0' ? '' : title
}

const cleanOptionalText = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined

const cleanOptionalPositiveNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const normalizeNavigationItems = (
  value: unknown,
  parent = 0,
  nextId = { current: 1 },
): tMenu => {
  if (!Array.isArray(value)) return []

  return value.flatMap(rawItem => {
    if (!isRecord(rawItem)) return []

    const id = cleanOptionalPositiveNumber(rawItem.id) || nextId.current
    nextId.current = Math.max(nextId.current, id + 1)

    const children = normalizeNavigationItems(rawItem.children, id, nextId)
    const title = cleanNavigationTitle(rawItem.title)
    const href = cleanOptionalText(rawItem.href)
    const path = cleanOptionalText(rawItem.path)
    const imageSrc = cleanOptionalText(rawItem.imageSrc)
    const imageAlt = cleanOptionalText(rawItem.imageAlt)
    const menueId = cleanOptionalPositiveNumber(rawItem.menueId)
    const brand = cleanOptionalPositiveNumber(rawItem.brand)

    if (!title && !href && !menueId && !brand) {
      return children.map(child => ({
        ...child,
        parent,
      }))
    }

    const normalizedItem: item = {
      id,
      parent: cleanOptionalPositiveNumber(rawItem.parent) || parent,
      title,
      ...(menueId ? { menueId } : {}),
      ...(brand ? { brand } : {}),
      ...(href ? { href } : {}),
      ...(path ? { path } : {}),
      ...(imageSrc ? { imageSrc } : {}),
      ...(imageAlt ? { imageAlt } : {}),
      ...(children.length ? { children } : {}),
    }

    return [normalizedItem]
  })
}

const normalizePaths = (value: unknown): tMenu => {
  const normalizedPaths = normalizeNavigationItems(value)
  if (normalizedPaths.length > 0) return normalizedPaths

  return isMenu(value, false) ? value : []
}

const readCachedPaths = () => {
  if (typeof window === 'undefined') return []

  try {
    return normalizePaths(JSON.parse(window.sessionStorage.getItem(navigationCacheKey) || 'null'))
  } catch (_error) {
    return []
  }
}

const writeCachedPaths = (paths: tMenu) => {
  if (typeof window === 'undefined' || paths.length === 0) return

  try {
    window.sessionStorage.setItem(navigationCacheKey, JSON.stringify(paths))
  } catch (_error) {
    // Cache ist nur Komfort, die Navigation funktioniert auch ohne.
  }
}

const fetchCurrentPaths = async () => {
  const currentPaths = await post({
    url: `menuTree/${process.env.NEXT_PUBLIC_MENU}`,
    body: config.shop,
  })

  const nextPaths = normalizePaths(currentPaths)

  if (nextPaths.length === 0) {
    console.error('Konnte Menü nicht richtig vom Server empfangen!', currentPaths)
    return []
  }

  return nextPaths
}

const useNavigationPaths = () => {
  const buildPaths = normalizePaths(Build.paths)
  const [paths, setPaths] = useState<tMenu>(buildPaths)

  useEffect(() => {
    (async () => {
      if (buildPaths.length === 0) {
        const cachedPaths = readCachedPaths()
        if (cachedPaths.length > 0) setPaths(cachedPaths)

        const nextPaths = await fetchCurrentPaths()
        if (nextPaths.length > 0) {
          writeCachedPaths(nextPaths)
          setPaths(nextPaths)
        }
        return
      }

      const menuChanged = new Date((
        (await get({
          url: `menuTree/${process.env.NEXT_PUBLIC_MENU}`,
        })) as { changedDate: string }
      )?.changedDate)

      const buildDate = new Date(Build.buildDate as string)

      if (menuChanged < buildDate) return

      const nextPaths = await fetchCurrentPaths()
      if (nextPaths.length === 0) return

      writeCachedPaths(nextPaths)
      setPaths(nextPaths)
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return paths
}

export default useNavigationPaths
